/*  /frontend/src/pages/Login.js  (v2.0 – modernized UI)
    All logic identical to v1.
------------------------------------------------------------------ */
import '../custom.css'

import React, { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { Spinner } from 'react-bootstrap'
import { CheckCircleFill, StarFill } from 'react-bootstrap-icons'
import { BIO_HOST } from '../utils/nameClaim'
import {
  claimFlowFrom, inClaimFlow, claimPayload, carryQuery, saveClaimWelcome, goTo, STUDIO_PATH,
} from '../utils/onboarding'

const logoUrl = '/assets/logo/greatly-social-full.png'

const PLATFORMS = [
  { name: 'Facebook',  url: 'https://img.icons8.com/color/96/facebook-new.png' },
  { name: 'Instagram', url: 'https://img.icons8.com/color/96/instagram-new.png' },
  { name: 'X',         url: 'https://img.icons8.com/color/96/twitterx--v1.png' },
  { name: 'LinkedIn',  url: 'https://img.icons8.com/color/96/linkedin.png' },
  { name: 'TikTok',    url: 'https://img.icons8.com/color/96/tiktok--v1.png' },
  { name: 'Pinterest', url: 'https://img.icons8.com/color/96/pinterest--v1.png' },
  { name: 'YouTube',   url: 'https://img.icons8.com/color/96/youtube-play.png' },
]

const inputStyle = {
  width        : '100%',
  padding      : '11px 14px',
  borderRadius : 9,
  border       : '1.5px solid #e5e7eb',
  fontSize     : 14,
  color        : '#111827',
  outline      : 'none',
  background   : '#ffffff',
  transition   : 'border-color 0.15s',
  boxSizing    : 'border-box',
}

export default function Login () {
  const [email,    setEmail]    = useState('')
  const [password, setPassword] = useState('')
  const [showPw,   setShowPw]   = useState(false)
  const [loading,  setLoading]  = useState(false)
  const [message,  setMessage]  = useState({ text: '', variant: 'success' })
  // From the name generators' sign-up page (T24): /login?claim=…&from=…&kit=…
  // saves the name, shortlist and kit to the existing account, then opens AI Studio.
  const [search]                = useState(() => (typeof window !== 'undefined' ? window.location.search : ''))
  const [flow]                  = useState(() => claimFlowFrom(search))
  const claimMode               = inClaimFlow(flow)

  const navigate = useNavigate()

  const handleSubmit = async e => {
    e.preventDefault()
    setMessage({ text: '', variant: 'success' })
    setLoading(true)
    try {
      const url = `${process.env.REACT_APP_BACKEND_URL}/api/auth/login`
      const res  = await fetch(url, {
        method     : 'POST',
        headers    : { 'Content-Type': 'application/json' },
        body       : JSON.stringify({ email, password, ...(claimMode ? claimPayload(flow) : {}) }),
        credentials: 'include',
      })
      const data = await res.json()
      if (res.ok && claimMode) {
        setMessage({ text: 'Logged in! Opening your dashboard…', variant: 'success' })
        await saveClaimWelcome({
          flow,
          claimResult: data.claim,
          fetchKit: async kitId => {
            const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/ai/profile-kit/${encodeURIComponent(kitId)}`, {
              credentials: 'include',
              headers    : data.token ? { Authorization: `Bearer ${data.token}` } : {},
            })
            if (!r.ok) throw new Error(`kit ${r.status}`)
            return r.json()
          },
        })
        goTo(STUDIO_PATH)
      } else if (res.ok) {
        setMessage({ text: 'Logged in! Redirecting…', variant: 'success' })
        setTimeout(() => { window.location.href = '/dashboard' }, 200)
      } else {
        setMessage({ text: data.message || 'Incorrect email or password.', variant: 'danger' })
      }
    } catch (err) {
      console.error('[LOGIN] network error', err)
      setMessage({ text: 'Network error – please try again.', variant: 'danger' })
    } finally {
      setLoading(false)
    }
  }

  return (
    <div style={{ minHeight:'100vh', display:'flex', fontFamily:"'DM Sans', system-ui, sans-serif" }}>

      {/* ── Left: Form ── */}
      <div style={{ flex:'1 1 480px', display:'flex', flexDirection:'column', justifyContent:'center', alignItems:'center', padding:'48px 32px', background:'#ffffff' }}>
        <div style={{ width:'100%', maxWidth:420 }}>

          {/* Logo */}
          <img src={logoUrl} alt='Greatly Social' style={{ height:36, marginBottom:36 }} />

          {/* Heading */}
          <h1 style={{ fontSize:26, fontWeight:900, color:'#111827', marginBottom:6 }}>Welcome back</h1>
          <p style={{ fontSize:14, color:'#6b7280', marginBottom:28 }}>
            Don't have an account?{' '}
            <Link to={`/register${carryQuery(search)}`} style={{ color:'#2563eb', fontWeight:700, textDecoration:'none' }}>Start your free trial</Link>
          </p>

          {flow.claim && (
            <div data-testid='claim-flow' style={{ display:'flex', alignItems:'center', gap:10, background:'#e8f5ee', border:'1px solid #a3cfbb', borderRadius:10, padding:'12px 14px', marginBottom:20 }}>
              <span aria-hidden='true' style={{ width:10, height:10, borderRadius:5, background:'#198754', flex:'none' }} />
              <span style={{ fontSize:14, color:'#0f5132' }}>
                Log in and we'll save <strong>{BIO_HOST}{flow.claim}</strong> to your account.
              </span>
            </div>
          )}

          {/* Alert */}
          {message.text && (
            <div style={{
              display     : 'flex',
              alignItems  : 'center',
              gap         : 8,
              padding     : '11px 14px',
              borderRadius: 9,
              marginBottom: 20,
              fontSize    : 13,
              fontWeight  : 600,
              background  : message.variant === 'success' ? '#dcfce7' : '#fef2f2',
              border      : `1px solid ${message.variant === 'success' ? '#bbf7d0' : '#fecaca'}`,
              color       : message.variant === 'success' ? '#16a34a' : '#dc2626',
            }}>
              {message.variant === 'success'
                ? <CheckCircleFill size={14} />
                : <i className='fas fa-exclamation-circle' style={{ fontSize:13 }} />
              }
              {message.text}
            </div>
          )}

          {/* Form */}
          <form onSubmit={handleSubmit}>
            <div style={{ marginBottom:16 }}>
              <label style={{ fontSize:13, fontWeight:600, color:'#374151', display:'block', marginBottom:6 }}>Email address</label>
              <input
                type='email'
                autoComplete='username'
                placeholder='you@company.com'
                value={email}
                onChange={e => setEmail(e.target.value)}
                required
                style={inputStyle}
                onFocus={e => e.target.style.borderColor='#2563eb'}
                onBlur={e => e.target.style.borderColor='#e5e7eb'}
              />
            </div>

            <div style={{ marginBottom:24 }}>
              <div style={{ display:'flex', justifyContent:'space-between', marginBottom:6 }}>
                <label style={{ fontSize:13, fontWeight:600, color:'#374151' }}>Password</label>
                <Link to='/forgot-password' style={{ fontSize:12, color:'#2563eb', fontWeight:600, textDecoration:'none' }}>Forgot password?</Link>
              </div>
              <div style={{ position:'relative' }}>
                <input
                  type={showPw ? 'text' : 'password'}
                  autoComplete='current-password'
                  placeholder='••••••••'
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  required
                  style={{ ...inputStyle, paddingRight:50 }}
                  onFocus={e => e.target.style.borderColor='#2563eb'}
                  onBlur={e => e.target.style.borderColor='#e5e7eb'}
                />
                <button
                  type='button'
                  onClick={() => setShowPw(p => !p)}
                  style={{ position:'absolute', right:12, top:'50%', transform:'translateY(-50%)', background:'transparent', border:'none', cursor:'pointer', fontSize:12, fontWeight:600, color:'#6b7280' }}
                >
                  {showPw ? 'Hide' : 'Show'}
                </button>
              </div>
            </div>

            <button
              type='submit'
              disabled={loading}
              style={{
                width          : '100%',
                padding        : '13px',
                borderRadius   : 10,
                border         : 'none',
                background     : loading ? '#93c5fd' : '#2563eb',
                color          : '#ffffff',
                fontSize       : 15,
                fontWeight     : 700,
                cursor         : loading ? 'not-allowed' : 'pointer',
                display        : 'flex',
                alignItems     : 'center',
                justifyContent : 'center',
                gap            : 8,
                boxShadow      : '0 4px 14px rgba(37,99,235,0.3)',
                transition     : 'background 0.15s',
              }}
              onMouseEnter={e => { if (!loading) e.currentTarget.style.background='#1d4ed8' }}
              onMouseLeave={e => { if (!loading) e.currentTarget.style.background='#2563eb' }}
            >
              {loading ? <><Spinner animation='border' size='sm' style={{ width:14, height:14 }} /> Logging in…</> : 'Log In'}
            </button>
          </form>

          {/* Social proof */}
          <div style={{ marginTop:32, paddingTop:24, borderTop:'1px solid #f3f4f6', textAlign:'center' }}>
            <div style={{ display:'flex', justifyContent:'center', gap:3, marginBottom:6 }}>
              {[...Array(5)].map((_, i) => <StarFill key={i} size={12} color='#fbbf24' />)}
            </div>
            <p style={{ fontSize:12, color:'#9ca3af', margin:0 }}>
              Trusted by <strong style={{ color:'#374151' }}>10,000+</strong> businesses worldwide
            </p>
          </div>
        </div>
      </div>

      {/* ── Right: Brand panel (hidden on mobile) ── */}
      <div
        className='d-none d-md-flex'
        style={{
          flex           : '1 1 480px',
          background     : 'linear-gradient(160deg, #0f172a 0%, #1e3a5f 60%, #1d4ed8 100%)',
          display        : 'flex',
          flexDirection  : 'column',
          justifyContent : 'center',
          padding        : '60px 52px',
          position       : 'relative',
          overflow       : 'hidden',
        }}
      >
        {/* Decorative blobs */}
        <div style={{ position:'absolute', top:-100, right:-100, width:360, height:360, borderRadius:'50%', background:'rgba(37,99,235,0.2)', filter:'blur(60px)', pointerEvents:'none' }} />
        <div style={{ position:'absolute', bottom:-80, left:-80, width:300, height:300, borderRadius:'50%', background:'rgba(124,58,237,0.15)', filter:'blur(50px)', pointerEvents:'none' }} />

        <div style={{ position:'relative', zIndex:1 }}>
          <div style={{ fontSize:13, fontWeight:700, textTransform:'uppercase', letterSpacing:'0.09em', color:'rgba(255,255,255,0.45)', marginBottom:16 }}>
            Greatly Social
          </div>
          <h2 style={{ fontSize:32, fontWeight:900, color:'#ffffff', lineHeight:1.2, marginBottom:16 }}>
            Schedule smarter.<br />Grow faster.
          </h2>
          <p style={{ fontSize:16, color:'rgba(255,255,255,0.65)', lineHeight:1.7, marginBottom:36 }}>
            One dashboard for all your social media. AI-powered content, drag-and-drop scheduling, and auto-publishing to every platform.
          </p>

          {/* Platform icons */}
          <div style={{ display:'flex', gap:12, marginBottom:36, flexWrap:'wrap' }}>
            {PLATFORMS.map(p => (
              <div key={p.name} title={p.name} style={{ width:36, height:36, borderRadius:9, background:'rgba(255,255,255,0.1)', display:'flex', alignItems:'center', justifyContent:'center' }}>
                <img src={p.url} alt={p.name} style={{ width:22, height:22 }} />
              </div>
            ))}
          </div>

          {/* Testimonial */}
          <div style={{ background:'rgba(255,255,255,0.08)', border:'1px solid rgba(255,255,255,0.12)', borderRadius:14, padding:'20px 22px' }}>
            <div style={{ display:'flex', gap:3, marginBottom:10 }}>
              {[...Array(5)].map((_, i) => <StarFill key={i} size={12} color='#fbbf24' />)}
            </div>
            <p style={{ fontSize:14, color:'rgba(255,255,255,0.85)', lineHeight:1.6, margin:'0 0 12px', fontStyle:'italic' }}>
              "Greatly Social cut our scheduling time by 70% and our engagement is up across every platform."
            </p>
            <div style={{ fontSize:12, color:'rgba(255,255,255,0.5)', fontWeight:600 }}>
              Jamie Rivera — Marketing Manager, Fresh Fit Foods
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}